import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { adsUserSignup, validateAdsUsername } from '../../utils/api';

const initialFormState = {
  firstName: '',
  lastName: '',
  mobile: '',
  gender: '',
  username: '',
  password: '',
  confirmPassword: ''
};

const AdsSignup = () => {
  const [formValues, setFormValues] = useState(initialFormState);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameValidation, setUsernameValidation] = useState('');
  const navigate = useNavigate();
  const navigateTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (navigateTimerRef.current != null) {
        clearTimeout(navigateTimerRef.current);
        navigateTimerRef.current = null;
      }
    },
    []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
    if (name === 'username') {
      setUsernameValidation('');
    }
  };

  const validateUsername = async () => {
    const username = formValues.username.trim();
    if (!username) {
      setUsernameValidation('');
      return { valid: false, message: 'Username is required.' };
    }

    setIsCheckingUsername(true);
    try {
      const response = await validateAdsUsername(username);
      const message = response?.data?.message || 'Valid Username';
      setUsernameValidation(message);
      return { valid: true, message };
    } catch (err) {
      const message = err.response?.data?.message || 'Username validation failed.';
      setUsernameValidation(message);
      return { valid: false, message };
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const { firstName, lastName, mobile, gender, username, password, confirmPassword } = formValues;

    if (!firstName || !lastName || !mobile || !gender || !username || !password) {
      setError('All fields are required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Password and confirm password must match.');
      return;
    }

    const usernameCheck = await validateUsername();
    if (!usernameCheck.valid) {
      setError(usernameCheck.message || 'Please enter a valid username.');
      return;
    }

    setIsLoading(true);
    try {
      await adsUserSignup({
        firstName,
        lastName,
        mobile,
        gender,
        username,
        password
      });
      setSuccess('Signup successful! Redirecting to login...');
      setFormValues(initialFormState);

      if (navigateTimerRef.current != null) clearTimeout(navigateTimerRef.current);
      navigateTimerRef.current = setTimeout(() => {
        navigateTimerRef.current = null;
        navigate('/ads/login');
      }, 1500);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Ads signup error:', err);
      setError(err.response?.data?.message || 'Failed to sign up. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Typography variant="h5" gutterBottom align="center">
          Ad Creator Signup
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <TextField
            label="First Name"
            name="firstName"
            value={formValues.firstName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isLoading}
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formValues.lastName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isLoading}
          />
          <TextField
            label="Mobile"
            name="mobile"
            value={formValues.mobile}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isLoading}
          />
          <TextField
            select
            label="Gender"
            name="gender"
            value={formValues.gender}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isLoading}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
          <TextField
            label="Username"
            name="username"
            value={formValues.username}
            onChange={handleChange}
            onBlur={validateUsername}
            fullWidth
            margin="normal"
            required
            disabled={isLoading || isCheckingUsername}
            helperText={usernameValidation || ' '}
            error={Boolean(usernameValidation) && usernameValidation !== 'Valid Username'}
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            value={formValues.password}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isLoading}
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            value={formValues.confirmPassword}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isLoading}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default AdsSignup;
